import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { transfersApi } from '@/services/transfers.api';
import { tenancyApi } from '@/services/tenancy.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';

const QUERY_KEY = ['transfers', 'pending'];

/** The queue a destination campus's own leader works from — every request
 *  waiting on a decision, tenant-wide or scoped to their own campuses,
 *  entirely by what their role already grants. */
export function PendingTransfersPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: ({ signal }) => transfersApi.listPending(signal),
  });
  const campuses = useQuery({
    queryKey: ['tenancy', 'campuses'],
    queryFn: ({ signal }) => tenancyApi.listCampuses(signal),
  });

  const campusName = (id: string | null): string =>
    id
      ? (campuses.data?.find((campus) => campus.id === id)?.name ?? id)
      : t('people.transfer.noCampus');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY, exact: false });

  const decide = useMutation({
    mutationFn: (input: { transferId: string; action: 'approve' | 'reject' }) =>
      input.action === 'approve'
        ? transfersApi.approve(input.transferId, { note: null })
        : transfersApi.reject(input.transferId, { note: null }),
    onSuccess: () => void invalidate(),
  });

  const transfers = query.data ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('people.transfer.pendingTitle')}</h1>
        </div>
      </header>

      {decide.isError && (
        <Alert tone="danger">
          {decide.error instanceof ApiError
            ? t(`errors.${decide.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {(query.isPending || campuses.isPending) && <Spinner label={t('common.states.loading')} />}
      {query.isSuccess && transfers.length === 0 && (
        <EmptyState title={t('people.transfer.pendingEmpty')} />
      )}

      {transfers.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('people.person.singular')}</th>
                <th scope="col">{t('people.transfer.fromTo')}</th>
                <th scope="col">{t('people.transfer.reason')}</th>
                <th scope="col">{t('common.labels.createdAt')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {transfers.map((transfer) => (
                <tr key={transfer.id}>
                  <td>
                    <Link to={`/people/${transfer.personId}`} className="person-name">
                      {transfer.personDisplayName}
                    </Link>
                  </td>
                  <td dir="ltr">
                    {campusName(transfer.fromCampusId)} → {campusName(transfer.toCampusId)}
                  </td>
                  <td>{transfer.reason}</td>
                  <td dir="ltr">{formatDate(transfer.requestedAt, locale)}</td>
                  <td>
                    {can('people.transfer.manage') && (
                      <>
                        <Button
                          variant="primary"
                          loading={decide.isPending}
                          onClick={() =>
                            decide.mutate({ transferId: transfer.id, action: 'approve' })
                          }
                        >
                          {t('common.actions.approve')}
                        </Button>
                        <Button
                          variant="danger"
                          loading={decide.isPending}
                          onClick={() =>
                            decide.mutate({ transferId: transfer.id, action: 'reject' })
                          }
                        >
                          {t('common.actions.reject')}
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
